import React, { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { RoleModel } from '../../service/role'
import { TimeoffCategoryModel } from '../../service/timeoffCategory'
import {
  CloseButton,
  Popover,
  PopoverButton,
  PopoverPanel
} from '@headlessui/react'
import { useTranslation } from 'react-i18next'
import { AnchorProps } from '@headlessui/react/dist/internal/floating'
import { Form, Formik } from 'formik'
import { CFields } from '../form/Form'
import {
  CFieldProps,
  ClientModel,
  countries,
  get_request_api,
  UserModel,
  ServiceModel
} from '@velocity/shared'
import { MdOutlineFilterList } from 'react-icons/md'

type Props = {
  fields: (CFieldProps | string)[]
  values?: Record<string, any>
  action: (val: Record<string, any>) => void
  anchor?: AnchorProps
}
const FilterForm = ({ fields, values, action, anchor }: Props) => {
  const { t } = useTranslation()
  const { data: role } = useQuery({
    queryKey: [
      'role',
      {
        minimal: true,
        filter: {},
        sort_field: 'name',
        sort_order: 'asc'
      }
    ],
    queryFn: get_request_api,
    enabled: fields.some((f) => f === 'role_id')
  })
  const { data: location } = useQuery({
    queryKey: [
      'location',
      {
        minimal: true,
        filter: {},
        sort_field: 'name',
        sort_order: 'asc'
      }
    ],
    queryFn: get_request_api,
    enabled: fields.some((f) => f === 'location_id')
  })
  const { data: service } = useQuery({
    queryKey: [
      'service',
      {
        minimal: true,
        filter: {},
        sort_field: 'name',
        sort_order: 'asc'
      }
    ],
    queryFn: get_request_api,
    enabled: fields.some((f) => f === 'service_id')
  })
  const { data: user } = useQuery({
    queryKey: [
      'user',
      { minimal: true, filter: {}, sort_field: 'firstname', sort_order: 'asc' }
    ],
    queryFn: get_request_api,
    enabled: fields.some(
      (f) =>
        typeof f === 'string' &&
        [
          'user_id',
          'email',
          'staff_id',
          'schedule_staff_id',
          'created_by'
        ].includes(f)
    )
  })
  const { data: timeoff_categories } = useQuery({
    queryKey: [
      'timeoff-category',
      {
        minimal: true,
        filter: {},
        sort_field: 'name',
        sort_order: 'asc'
      }
    ],
    queryFn: get_request_api,
    enabled: fields.some((f) => f === 'time_off_category_id')
  })
  const sampleFields: Record<string, CFieldProps> = useMemo(() => {
    return {
      status: {
        name: 'status',
        label: 'Status',
        options: [
          { value: true, label: t('Active') },
          { value: false, label: t('Inactive') }
        ]
      },
      status1: {
        name: 'status',
        label: 'Status',
        options: [
          { value: 'pending', label: t('Pending') },
          { value: 'approved', label: t('Approved') },
          { value: 'rejected', label: t('Rejected') }
        ]
      },
      timesheet_status: {
        name: 'status',
        label: 'Status',
        options: [
          {
            value: 'pending_overtime_approval',
            label: t('Pending Overtime Approval')
          },
          { value: 'pending', label: t('Pending') },
          { value: 'accepted', label: t('Accepted') },
          { value: 'clocked_in', label: t('Clocked In') },
          { value: 'completed', label: t('Completed') }
        ]
      },
      schedule_status: {
        name: 'status',
        label: 'Schedule Status',
        options: [
          { value: 'draft', label: t('Draft') },
          { value: 'published', label: t('Published') },
          { value: 'completed', label: t('Completed') }
        ]
      },
      payment_status: {
        name: 'payment_status',
        label: 'Payment Status',
        options: [
          { value: true, label: t('Paid') },
          { value: false, label: t('Unpaid') }
        ]
      },
      paid: {
        name: 'paid',
        label: 'Type',
        options: [
          { value: true, label: t('Paid') },
          { value: false, label: t('Unpaid') }
        ]
      },
      type: {
        name: 'type',
        label: 'Type',
        options: [
          { value: 'physical', label: t('Physical') },
          { value: 'virtual', label: t('Virtual') }
        ]
      },
      method: {
        name: 'method',
        label: 'Method',
        options: [
          { value: 'POST', label: t('Create') },
          { value: 'PUT', label: t('Update') },
          { value: 'DELETE', label: t('Delete') }
        ]
      },
      status_code: {
        name: 'status_code',
        label: 'Status Code',
        options: [
          { value: '200', label: '200' },
          { value: '400', label: '400' },
          { value: '401', label: '401' }
        ]
      },
      assignment: {
        name: 'assignment',
        label: 'Assignment',
        isMulti: false,
        options: [
          { value: 'assigned', label: t('Assigned') },
          { value: 'unassigned', label: t('Unassigned') }
        ]
      },
      country: {
        name: 'country',
        label: 'Country',
        options: countries.map((country) => {
          return {
            value: country,
            label: country
          }
        })
      },
      currency: {
        name: 'currency',
        label: 'Currency',
        options: Intl.supportedValuesOf('currency').map((currency: any) => {
          return {
            value: currency,
            label: currency
          }
        })
      },
      role_id: {
        name: 'role_id',
        label: 'Role',
        options: role?.rows.map((r: RoleModel) => ({
          value: r.id,
          label: r.name
        }))
      },
      location_id: {
        name: 'location_id',
        label: 'Client',
        options: location?.rows.map((r: ClientModel) => ({
          value: r.id,
          label: r.name
        }))
      },
      service_id: {
        name: 'service_id',
        label: 'Service',
        options: service?.rows.map((r: ServiceModel) => ({
          value: r.id,
          label: r.name
        }))
      },
      user_id: {
        name: 'user_id',
        label: 'Staff',
        options: user?.rows.map((r: UserModel) => ({
          value: r.id,
          label: r.firstname + ' ' + r.lastname
        }))
      },
      staff_id: {
        name: 'staff_id',
        label: 'Staff',
        options: user?.rows.map((r: UserModel) => ({
          value: r.id,
          label: r.firstname + ' ' + r.lastname
        }))
      },
      created_by: {
        name: 'created_by',
        label: 'User',
        options: user?.rows.map((r: UserModel) => ({
          value: r.id,
          label: r.firstname + ' ' + r.lastname
        }))
      },
      schedule_staff_id: {
        name: 'staff_id',
        label: 'Staff',
        options: [
          { value: 'unassigned', label: 'Unassigned' },
          ...(user?.rows.map((r: UserModel) => ({
            value: r.id,
            label: r.firstname + ' ' + r.lastname
          })) || [])
        ]
      },
      email: {
        name: 'email',
        label: 'Staff',
        options: user?.rows.map((r: UserModel) => ({
          value: r.email,
          label: r.firstname + ' ' + r.lastname
        }))
      },
      time_off_category_id: {
        name: 'time_off_category_id',
        label: 'Time off category',
        options: timeoff_categories?.rows.map((r: TimeoffCategoryModel) => ({
          value: r.id,
          label: r.name
        }))
      }
    }
    // eslint-disable-next-line
  }, [role, location, service, user, timeoff_categories])
  const flds = useMemo(() => {
    return fields.map((f: CFieldProps | string, i: number) => {
      let field: CFieldProps
      if (typeof f === 'string') {
        field = { ...sampleFields[f] }
        field.type = 'select'
      } else field = f
      return field
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, sampleFields])
  const handleSubmit = (val: Record<string, any>) => {
    action(val)
  }
  return (
    <Popover className='relative inline-block text-left'>
      <PopoverButton className='gap-x-2 items-center inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-1.5 bg-primary font-medium  hover:bg-primary focus:outline-none focus:ring-2 btn-primary focus:ring-primary focus:ring-offset-2 focus:ring-offset-gray-100 text-white'>
        {t('Filter')}
        <MdOutlineFilterList size={20} />
      </PopoverButton>
      <PopoverPanel
        anchor={anchor || 'bottom start'}
        transition
        className='absolute z-10 mt-2 w-72 origin-top-left divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in'
      >
        <Formik
          initialValues={values || {}}
          enableReinitialize={true}
          onSubmit={handleSubmit}
        >
          <Form className='m-2 space-y-2'>
            <CFields fields={flds} />
            <div className='my-2 flex gap-2'>
              <CloseButton
                type='submit'
                className='my-2 text-primary hover:text-opacity-75 w-full py-2 border border-primary rounded-md'
                onClick={() => {
                  handleSubmit({})
                }}
              >
                {t('Reset')}
              </CloseButton>
              <CloseButton
                type='submit'
                className='my-2 bg-primary hover:bg-opacity-75 w-full text-white py-2 rounded-md'
              >
                {t('Filter')}
              </CloseButton>
            </div>
          </Form>
        </Formik>
      </PopoverPanel>
    </Popover>
  )
}

export default FilterForm
